/**
 * Formats a number according to a given locale with exactly 2 decimal places.
 * @param {number} num - The number to format.
 * @param {string} [locale=USER_LOCALE] - Optional locale string (e.g., 'en-US', 'fr-FR'). Defaults to USER_LOCALE.
 * @returns {string} The formatted number as a string with 2 decimal places.
 *
 * @example
 * numberFormatter(1234.5); // "1,234.50" (depending on USER_LOCALE)
 * numberFormatter(1234.567, 'en-US'); // "1,234.57"
 * numberFormatter(1234, 'fr-FR'); // "1 234,00"
 */
export const numberFormatter = (num, locale = "en-US") => new Intl.NumberFormat(locale, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(num)

/**
 * Formats a date string or Date object as "Mon DD, YYYY".
 * Returns empty string if input is null/undefined/invalid.
 * @param {string | Date | null | undefined} date
 * @returns {string}
 */
export const formatDateLong = (date) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj)) return '';

  return dateObj.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

/**
 * Capitalizes the first letter of each word in a string.
 * Words are assumed to be separated by spaces.
 * Returns an empty string if input is falsy.
 *
 * @param {string} str - The string to capitalize.
 * @returns {string} The string with each word capitalized.
 *
 * @example
 * capitalizeWords("hello world"); // "Hello World"
 * capitalizeWords("javascript"); // "Javascript"
 * capitalizeWords(""); // ""
 */
export const capitalizeWords = (str) => {
  if (!str) return '';
  
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Creates words from hyphenated string.
 * Words are assumed to be separated by hyphen (-).
 *
 * @param {string} hypenText - The string to separated by hyphens.
 * @returns {string} The string with each word capitalized.
 *
 * @example
 * capitalizeWords("hello-world"); // "Hello World"
 * capitalizeWords("javascript-language"); // "Javascript Language"
 */
export const createWordFromHyphenText = hypenText => hypenText.split('-')
  .map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')


/**
 * Retrieves an element from the array in a cyclic manner based on the given step.
 * If the step exceeds the array length, it wraps around using modulo.
 *
 * @param {Array} arr - The non-empty array to cycle through.
 * @param {number} step - The step index to retrieve the element (can be larger than array length).
 * @returns {*} The element at the cyclic position in the array.
 *
 * @throws {Error} Throws an error if the array is empty or not an array.
 *
 * @example
 * cycleArrayElements([1, 2, 3], 0); // 1
 * cycleArrayElements([1, 2, 3], 4); // 2 (since 4 % 3 = 1)
 * cycleArrayElements(['a', 'b', 'c'], 7); // 'b' (since 7 % 3 = 1)
 */
export const cycleArrayElements = (arr, step) => {
  if (!Array.isArray(arr) || arr.length === 0) {
    throw new Error("Array must be non-empty");
  }
  return arr[step % arr.length]
}

/**
 * Truncates a number to two decimal places without rounding.
 *
 * @param {number} value - The number to truncate.
 * @returns {number} The truncated number with two decimal places.
 *
 * @example
 * truncateToTwoDecimals(2.595206); // 2.59
 * truncateToTwoDecimals(2.999); // 2.99
 * truncateToTwoDecimals(-2.595206); // -2.59
 */
export const truncateToTwoDecimals = value => {
  const [integer, decimal = ''] = value.toString().split('.')

  // Take first two digits of decimal part (pad with zeros if needed)
  const truncatedDecimal = (decimal + '00').slice(0, 2)

  // Combine and convert back to number
  return Number(`${integer}.${truncatedDecimal}`)
}

